/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import InspirationalSearchCard from '@/components/organisms/inspirational-search-card/inspirational-search-card';
import styles from './inspirational-search-flow-question.module.scss';

export interface InspirationalSearchFlowQuestionProps {
  /** The text to show as a headline */
  headline?: string;
  /** The maximum options allowed */
  maxOptions?: number;
  /** An event that is triggered on click */
  onChange?: (value: number[]) => void;
  /** The list of options to show */
  options?: {
    headline: string;
    id: number;
    image?: Content.Properties.Image | null;
    subHeadline?: string;
  }[];
  /** The text to show as a sub headline */
  subHeadline?: string;
  /** The value of the question */
  value?: number[];
}

const InspirationalSearchFlowQuestion = ({
  headline,
  maxOptions = 0,
  onChange,
  options = [],
  subHeadline,
  value = [],
}: InspirationalSearchFlowQuestionProps) => {
  const [shakingCard, setShakingCard] = useState<number>();

  useEffect(() => {
    if (typeof shakingCard !== 'number') return;

    setTimeout(() => {
      setShakingCard(undefined);
    }, 250);
  }, [shakingCard]);

  return (
    <div className={classNames(styles.component, {})}>
      {(headline || subHeadline) && (
        <div className={styles.headlineWrapper}>
          {headline && (
            <Typography className={styles.headline} tagStyle="headingMedium">
              {headline}
            </Typography>
          )}

          {subHeadline && (
            <Typography tagStyle="bodyLarge">{subHeadline}</Typography>
          )}
        </div>
      )}

      <motion.div
        animate="show"
        className={styles.stepOptions}
        initial="hidden"
        variants={{
          hidden: { opacity: 0 },
          show: {
            opacity: 1,
            transition: {
              delay: 0.25,
              duration: 0.25,
              staggerChildren: 0.125,
            },
          },
        }}
      >
        {options?.map((option, optionIndex) => {
          return (
            <motion.span
              key={optionIndex}
              className={classNames(styles.stepOption, {
                [styles.hasImage]: option.image,
                [styles.hasSubHeadline]: option.subHeadline,
                [styles.isShaking]: shakingCard === option.id,
              })}
              variants={{
                hidden: { opacity: 0, translateY: 10 },
                show: { opacity: 1, translateY: 0 },
              }}
            >
              <InspirationalSearchCard
                headline={option.headline}
                image={option.image ?? undefined}
                onClick={() => {
                  if (maxOptions === 1) {
                    onChange?.([option.id]);
                  } else {
                    if (value.includes(option.id)) {
                      onChange?.(value.filter(item => item !== option.id));
                    } else {
                      if (maxOptions > 0 && maxOptions <= value.length) {
                        setShakingCard(option.id);

                        return;
                      }

                      onChange?.([...value, option.id]);
                    }
                  }
                }}
                selected={value.includes(option.id)}
                subHeadline={option.subHeadline}
              />
            </motion.span>
          );
        })}
      </motion.div>
    </div>
  );
};

InspirationalSearchFlowQuestion.displayName = 'InspirationalSearchFlowQuestion';

export default InspirationalSearchFlowQuestion;
